import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchAmbassadorData, updateAmbassadorData } from '../api/contracts.js';
import { queryKeys } from '../lib/queryKeys';
import { PAYMENT_LABELS } from '../lib/paymentMethod';
import { useToast } from '../contexts/ToastContext';

const modalStyles = `
  .ambassador-data-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }
  .ambassador-data-modal {
    background: #ffffff;
    border-radius: 14px;
    padding: 28px;
    max-width: 540px;
    width: 90%;
    max-height: 85vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(15, 23, 42, 0.18);
    color: #0f172a;
  }
  .ambassador-data-modal__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    gap: 12px;
  }
  .ambassador-data-modal__title {
    font-size: 18px;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
  }
  .ambassador-data-modal__header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .ambassador-data-modal__icon-btn {
    background: #f3f4f6;
    border: none;
    color: #374151;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    padding: 6px 12px;
    border-radius: 8px;
    transition: background 0.15s;
  }
  .ambassador-data-modal__icon-btn:hover {
    background: #e5e7eb;
  }
  .ambassador-data-modal__close {
    background: none;
    border: none;
    font-size: 22px;
    cursor: pointer;
    color: #9ca3af;
    padding: 2px 8px;
    line-height: 1;
  }
  .ambassador-data-modal__close:hover {
    color: #0f172a;
  }
  .ambassador-data-modal__locked {
    font-size: 12px;
    color: #9ca3af;
    margin-bottom: 12px;
    padding: 8px 12px;
    background: #f9fafb;
    border-radius: 6px;
    border-left: 3px solid #d1d5db;
  }
  .ambassador-data-field {
    margin-bottom: 14px;
  }
  .ambassador-data-field__label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  .ambassador-data-field__value {
    font-size: 14px;
    color: #0f172a;
    word-break: break-word;
  }
  .ambassador-data-field__input,
  .ambassador-data-field__select {
    width: 100%;
    padding: 9px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: #ffffff;
    color: #0f172a;
    font-size: 14px;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
  }
  .ambassador-data-field__input:focus,
  .ambassador-data-field__select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
  .ambassador-data-divider {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 16px 0;
  }
  .ambassador-data-modal__actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    margin-top: 22px;
  }
  .ambassador-data-modal__btn {
    padding: 9px 18px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }
  .ambassador-data-modal__btn--primary {
    background: #3b82f6;
    color: #fff;
  }
  .ambassador-data-modal__btn--primary:hover:not(:disabled) {
    background: #2563eb;
  }
  .ambassador-data-modal__btn--primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .ambassador-data-modal__btn--secondary {
    background: #f3f4f6;
    color: #374151;
  }
  .ambassador-data-modal__btn--secondary:hover {
    background: #e5e7eb;
  }
`;

const LOCKED_STATUSES = new Set(['sent_to_sign', 'sent_to_docusign', 'completed']);

const toIsoDate = (val) => {
  if (!val) return '';
  try {
    const d = new Date(val);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  } catch {
    return '';
  }
};

function AmbassadorDataModal({ contractId, onClose }) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['contract-detail', contractId],
    queryFn: () => fetchAmbassadorData(contractId),
    enabled: !!contractId,
  });

  const amb = data?.ambassadorData;
  const status = data?.status;
  const canEdit = !!amb && !LOCKED_STATUSES.has(status);

  useEffect(() => {
    if (amb && isEditing && !form) {
      setForm({
        fullName: amb.fullName || '',
        email: amb.email || '',
        phone: amb.phone || '',
        passportNumber: amb.passportNumber || '',
        passportIssuedBy: amb.passportIssuedBy || '',
        passportIssueDate: toIsoDate(amb.passportIssueDate),
        address: amb.address || '',
        paymentMethod: amb.paymentMethod || 'paypal',
        paymentAddress: amb.paymentAddress || '',
      });
    }
  }, [amb, isEditing, form]);

  const mutation = useMutation({
    mutationFn: (payload) => updateAmbassadorData(contractId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-detail', contractId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
      addToast('Сохранено', 'Данные амбассадора обновлены', 'success');
      setIsEditing(false);
      setForm(null);
    },
    onError: (err) => {
      addToast('Ошибка', err?.response?.data?.error || 'Не удалось сохранить изменения', 'error');
    },
  });

  const formatDate = (val) => {
    if (!val) return '---';
    try { return new Date(val).toLocaleDateString('ru-RU'); } catch { return val; }
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!form) return;
    mutation.mutate(form);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setForm(null);
  };

  return (
    <>
      <style>{modalStyles}</style>
      <div className="ambassador-data-modal-overlay">
        <div className="ambassador-data-modal">
          <div className="ambassador-data-modal__header">
            <h3 className="ambassador-data-modal__title">Данные амбассадора</h3>
            <div className="ambassador-data-modal__header-actions">
              {!isEditing && canEdit && (
                <button
                  type="button"
                  className="ambassador-data-modal__icon-btn"
                  onClick={() => setIsEditing(true)}
                >
                  Редактировать
                </button>
              )}
              <button className="ambassador-data-modal__close" onClick={onClose}>&times;</button>
            </div>
          </div>

          {isLoading && <p style={{ color: '#6b7280', fontSize: 14 }}>Загрузка...</p>}
          {error && <p style={{ color: '#dc2626', fontSize: 14 }}>Ошибка загрузки данных</p>}
          {!isLoading && !amb && !error && <p style={{ color: '#6b7280', fontSize: 14 }}>Данные формы ещё не заполнены</p>}

          {amb && !canEdit && !isEditing && (
            <div className="ambassador-data-modal__locked">
              Редактирование недоступно: договор уже отправлен на подпись или завершён.
            </div>
          )}

          {amb && !isEditing && (
            <>
              <DataField label="ФИО" value={amb.fullName} />
              <DataField label="Email" value={amb.email} />
              <DataField label="Телефон" value={amb.phone || '---'} />
              <hr className="ambassador-data-divider" />
              <DataField label="Номер паспорта" value={amb.passportNumber} />
              <DataField label="Кем выдан" value={amb.passportIssuedBy} />
              <DataField label="Дата выдачи" value={formatDate(amb.passportIssueDate)} />
              <hr className="ambassador-data-divider" />
              <DataField label="Адрес" value={amb.address} />
              <hr className="ambassador-data-divider" />
              <DataField label="Способ оплаты" value={PAYMENT_LABELS[amb.paymentMethod] || amb.paymentMethod} />
              <DataField label="Реквизиты" value={amb.paymentAddress} />
              <hr className="ambassador-data-divider" />
              <DataField label="Дата заполнения" value={formatDate(amb.submittedAt)} />
            </>
          )}

          {amb && isEditing && form && (
            <>
              <EditField label="ФИО" value={form.fullName} onChange={(v) => handleFieldChange('fullName', v)} />
              <EditField label="Email" value={form.email} onChange={(v) => handleFieldChange('email', v)} type="email" />
              <EditField label="Телефон" value={form.phone} onChange={(v) => handleFieldChange('phone', v)} />
              <hr className="ambassador-data-divider" />
              <EditField label="Номер паспорта" value={form.passportNumber} onChange={(v) => handleFieldChange('passportNumber', v)} />
              <EditField label="Кем выдан" value={form.passportIssuedBy} onChange={(v) => handleFieldChange('passportIssuedBy', v)} />
              <EditField label="Дата выдачи" value={form.passportIssueDate} onChange={(v) => handleFieldChange('passportIssueDate', v)} type="date" />
              <hr className="ambassador-data-divider" />
              <EditField label="Адрес" value={form.address} onChange={(v) => handleFieldChange('address', v)} />
              <hr className="ambassador-data-divider" />
              <div className="ambassador-data-field">
                <div className="ambassador-data-field__label">Способ оплаты</div>
                <select
                  className="ambassador-data-field__select"
                  value={form.paymentMethod}
                  onChange={(e) => handleFieldChange('paymentMethod', e.target.value)}
                >
                  <option value="paypal">PayPal</option>
                  <option value="crypto">Cryptocurrency</option>
                  <option value="payoneer">Payoneer</option>
                  <option value="garna">Garna</option>
                </select>
              </div>
              <EditField label="Реквизиты" value={form.paymentAddress} onChange={(v) => handleFieldChange('paymentAddress', v)} />

              <div className="ambassador-data-modal__actions">
                <button
                  type="button"
                  className="ambassador-data-modal__btn ambassador-data-modal__btn--secondary"
                  onClick={handleCancelEdit}
                  disabled={mutation.isPending}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="ambassador-data-modal__btn ambassador-data-modal__btn--primary"
                  onClick={handleSave}
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function DataField({ label, value }) {
  return (
    <div className="ambassador-data-field">
      <div className="ambassador-data-field__label">{label}</div>
      <div className="ambassador-data-field__value">{value}</div>
    </div>
  );
}

function EditField({ label, value, onChange, type = 'text' }) {
  return (
    <div className="ambassador-data-field">
      <div className="ambassador-data-field__label">{label}</div>
      <input
        className="ambassador-data-field__input"
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export default AmbassadorDataModal;
